import { api, isUnauthorizedAxiosError } from '@/lib/api.ts';
import type { QueryClient } from '@tanstack/react-query';
import type { APIUser } from '@/types/index.ts';

export async function fetchCurrentUser(): Promise<APIUser | null> {
  try {
    const { data } = await api.get<APIUser>('/public/1/auth/myself/', {
      skipAuthRedirect: true,
    });
    return data;
  } catch (error) {
    if (isUnauthorizedAxiosError(error)) {
      // Unauth Error = request OK, but you are not logged in.
      return null;
    }
    throw error;
  }
}

export async function login(credentials: { username: string; password: string }): Promise<APIUser> {
  const { data } = await api.post<APIUser>('/public/1/auth/login/', credentials);
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/public/1/auth/logout/');
}

export async function sudoLogin(adminId: number): Promise<APIUser> {
  const { data } = await api.get<APIUser>(`/internal/current/users/${adminId}/sudo/`);
  return data;
}

/**
 * Tears down all session-scoped state at an authentication boundary (login,
 * sudo, logout, session death). Every cached server query belongs to the
 * current user, so the whole query cache is dropped. The selected park is no
 * longer client state — it lives in the URL (`/park/$parkId`) — so a redirect to
 * `/login` is all that's needed to leave it behind. Lives here — rather than in
 * a hook — so the non-React axios interceptor can reuse it.
 */
export function resetSession(queryClient: QueryClient): void {
  queryClient.clear();
}
